import { CaratRight } from '@weee-fe/kunlunicon';
import { twMerge } from 'tailwind-merge';
import { CroppedImage } from '@weee-fe/module';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import BenefitsItemPopup from './benefits-item-popup';
import { getReferralIdFromCookie } from '@/utils/common/referral';
import { ClickType } from '@/constants/track';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getDayjsObject } from '@/utils/common/dayjs';
import { useRouter } from 'next/router';
import { isSayWeeeApp } from '@/utils/common/device';
import { formatTimeByTimezone } from '@/utils/timezone';
import { LA_TIMEZONE_ADDR } from '@/constants/help';

interface BenefitItem {
  type: string;
  icon: string;
  action_url: string;
  content: {
    title: string;
    sub_title: string;
  };
  details: any;
  available: boolean;
}

const BenefitsList = ({ className, isPC, data = {} }: { className?: string; isPC?: boolean; data?: any }) => {
  const { t, lang } = useTranslation('student');
  const { benefits_list } = data;
  const benefitsItemPopupRef = useRef<any>(null);
  const [benefitsItemPopupData, setBenefitsItemPopupData] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(false);
  const { buttonClick } = useClickActionT2();
  const router = useRouter();

  const handleClickBenefitsItem = (e, item: BenefitItem) => {
    e.stopPropagation();
    e.preventDefault();
    if (!item?.available || !benefitsItemPopupRef.current) return;
    const type = item.type ? item.type.toLowerCase() : '';
    new Promise(resolve => {
      buttonClick({
        co: {
          target_nm: type,
          click_type: ClickType.normal
        },
        ctx: {
          user_source: getReferralIdFromCookie() ? 'referred' : 'normal'
        }
      });
      resolve(true);
    }).then(() => {
      if (!!item.action_url) {
        if (isSayWeeeApp() || isPC) {
          window.location.href = item.action_url;
        } else {
          router.push(item.action_url);
        }
      } else {
        setBenefitsItemPopupData(item);
        benefitsItemPopupRef.current?.show(e);
      }
    });
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getCouponDate = (info: string, timestamp: number, type: string) => {
    if (!info || !timestamp) return '';
    if (type === 'POST_GOLD_REWARDS') {
      const dateStr = formatTimeByTimezone(timestamp, LA_TIMEZONE_ADDR, lang, 'MM/DD/YY');
      return info?.replace('%s', dateStr);
    }
    return info?.replace('%s', getDayjsObject(timestamp * 1000, lang).format('MM/DD/YY'));
  };

  if (!benefits_list?.length) return null;

  return (
    <div
      className={twMerge(
        'px-5 pb-4 pt-2 mb-2 border-b border-solid border-surface-100-hairline bg-white overflow-hidden',
        className
      )}>
      <div>
        {benefits_list.map((item, index) => (
          <div
            key={`${item.id}-${item.type}`}
            onClick={e => handleClickBenefitsItem(e, item)}
            className={twMerge(
              'flex items-center justify-between py-3 cursor-pointer',
              index !== benefits_list.length - 1 && 'border-b border-solid border-tint-black-25',
              !item?.available && 'cursor-default'
            )}>
            {/* Icon */}
            <div className="w-6 h-6 flex-shrink-0">
              <CroppedImage
                url={item.icon}
                alt={item.type}
                width={24}
                height={24}
                configs={{ renderWidth: 24, renderHeight: 24 }}
                wrapperClassName="w-6 h-6"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Text Content */}
            <div className="mx-3 flex-1 min-w-0 flex flex-col justify-center">
              <div
                className="enki-body-xs-medium text-primary-5"
                dangerouslySetInnerHTML={{ __html: item?.content?.title }}></div>
              <div
                className="flex items-center enki-body-3xs mt-1 text-surface-100-fg-minor"
                dangerouslySetInnerHTML={{
                  __html: isMounted
                    ? item?.available
                      ? getCouponDate(item?.content?.content, item?.expiration_timestamp, item?.type ?? '')
                      : item?.content?.content
                    : ''
                }}></div>
            </div>

            {/* Arrow */}
            {item?.available && (
              <div className="flex-shrink-0 w-6 h-6">
                <CaratRight fontSize={24} className="text-surface-100-fg-minor" />
              </div>
            )}
          </div>
        ))}
      </div>
      <BenefitsItemPopup ref={benefitsItemPopupRef} data={benefitsItemPopupData} isPC={isPC}></BenefitsItemPopup>
    </div>
  );
};

export default BenefitsList;
